import { z } from 'zod';
import { CLIENT_SOURCES, ClientType, CommercialCondition } from '../enums';
import { classifySpanishTaxId, taxIdMatchesType } from '../tax-id';
import { SPAIN_COUNTRY_CODE, isSpanishTerritory } from '../vat';
import {
  addressSchema,
  deliveryAddressSchema,
  cifVatSchema,
  cuidSchema,
  emailSchema,
  phoneSchema,
  vatNumberSchema,
} from './common.dto';

/** Client (clinic/doctor) DTOs. Agents own their own clients. */

/**
 * VAT input that ALSO accepts an empty string or null, both meaning "clear the VAT".
 * Without this, a client whose stored VAT became INVALID could never be saved after the
 * user removed the number (an omitted field fell back to the old value and re-failed VIES).
 */
const vatNumberInputSchema = z
  .union([vatNumberSchema, z.literal(''), z.null()])
  .transform((v) => (v ? v : null));

/** Base shape (kept as a plain object so `.partial()` and `.superRefine()` both compose). */
/** A billing entity other than the default: a legal entity with its own tax number. */
export const billingEntityInputSchema = z.object({
  id: cuidSchema.optional(),
  legalName: z.string().trim().min(1).max(200),
  tradeName: z.string().trim().max(200).optional(),
  cifVat: z.union([cifVatSchema, z.literal('')]).optional(),
  vatNumber: vatNumberInputSchema.optional(),
  billingAddress: addressSchema,
});
export type BillingEntityInputDto = z.infer<typeof billingEntityInputSchema>;

const clientObject = z.object({
  company: z.string().trim().min(1).max(200),
  /** COMPANY may carry a VAT number; PERSON has only the fiscal id. Default COMPANY. */
  type: z.nativeEnum(ClientType).default(ClientType.COMPANY),
  /** Commercial / trade name (companies; optional). */
  tradeName: z.string().trim().max(200).optional(),
  /**
   * Fiscal id (NIF/CIF for Spain, national number elsewhere). OPTIONAL for both types —
   * dedup is by email; a PERSON still needs one once their yearly turnover exceeds
   * €3000 (checked at order time, not here). '' explicitly clears a stored value.
   */
  cifVat: z.union([cifVatSchema, z.literal('')]).optional(),
  /** EU/non-EU VAT number — companies only, optional; VIES-validated server-side.
   *  Accepts '' / null to explicitly clear a previously-stored number. */
  vatNumber: vatNumberInputSchema.optional(),
  /** One e-mail (kept as a list for compatibility; the form carries one). Mandatory. */
  emails: z.array(emailSchema).min(1).max(10),
  /** Dial-code + national number (e.g. "+34 600000000"); loose charset, 6–40 chars. */
  /** Mandatory, international format (+34 …). */
  phone: phoneSchema,
  /** Contact person (reference form: "Persona de contacto" → Nombre + Apellido). */
  contactFirstName: z.string().trim().max(120).optional(),
  contactLastName: z.string().trim().max(120).optional(),
  /** "Nombre de la clínica / doctor". */
  clinicDoctorName: z.string().trim().max(200).optional(),
  /** "Número de colegiado" (professional registration number). */
  collegiateNumber: z.string().trim().max(60).optional(),
  billingAddress: addressSchema,
  /** Legacy single shipping address; when omitted, shipping == billing. */
  shippingAddress: addressSchema.optional(),
  /**
   * Managed list of delivery addresses. When omitted on create, the server seeds it
   * from shippingAddress (or a copy of billing when shipping == billing).
   */
  deliveryAddresses: z.array(deliveryAddressSchema).max(20).optional(),
  /**
   * Owning agent. AGENT callers always own what they create (this is ignored for
   * them); ADMIN / SUPER_ADMIN pick the agent on create (the UI requires it).
   */
  ownerAgentId: cuidSchema.optional(),
  /** Source / channel of acquisition (fixed list). Mandatory for new clients; existing ones may lack it. */
  sourceChannel: z.enum(CLIENT_SOURCES),
  /** Estandar · Largo plazo · Trainer — chosen on the client, not on the order. */
  commercialCondition: z.nativeEnum(CommercialCondition).optional(),
  /** Set by the Super Admin only (the server enforces it). */
  sepaEligible: z.boolean().optional(),
  /** Language of the messages sent to the client. */
  language: z.enum(['es', 'en']).nullable().optional(),
  /**
   * Additional billing entities (legal entities the proforma / invoice can be issued
   * to) besides the default one carried by the top-level fiscal fields. Replaces the
   * stored list when sent.
   */
  extraBillingEntities: z.array(billingEntityInputSchema).max(20).optional(),
});

/** PERSON has no VAT number. (The fiscal id is optional for both types.) */
function refineClient(
  val: {
    type?: ClientType;
    vatNumber?: string | null;
    cifVat?: string;
    billingAddress?: { countryCode?: string };
  },
  ctx: z.RefinementCtx,
): void {
  if (val.type === ClientType.PERSON && val.vatNumber) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['vatNumber'],
      message: 'A person has no VAT number — only a fiscal id.',
    });
  }
  // In Spain the client type must match the tax number (CIF → company, NIF / NIE → person).
  // An INVALID check digit is not a schema error: the record saves, the client is
  // blocked for orders and listed for correction.
  const cc = (val.billingAddress?.countryCode ?? SPAIN_COUNTRY_CODE).toUpperCase();
  if (val.type && val.cifVat && isSpanishTerritory(cc)) {
    const parsed = classifySpanishTaxId(val.cifVat);
    if (!taxIdMatchesType(parsed.kind, val.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cifVat'],
        message:
          parsed.kind === 'CIF'
            ? 'A CIF belongs to a company — change the client type to Company.'
            : 'A NIF / NIE belongs to a person — change the client type to Person.',
      });
    }
  }
}

export const createClientSchema = clientObject.superRefine(refineClient);
export type CreateClientDto = z.infer<typeof createClientSchema>;

export const updateClientSchema = clientObject
  .partial()
  .extend({
    /** Existing clients may still lack a source; null keeps it empty. */
    sourceChannel: z.enum(CLIENT_SOURCES).nullable().optional(),
    /**
     * Admin-only: reassign the owning agent (or `null` to unassign). Ignored for AGENT
     * callers — they can never change ownership. Enforced server-side.
     */
    ownerAgentId: cuidSchema.nullable().optional(),
  })
  .superRefine(refineClient);
export type UpdateClientDto = z.infer<typeof updateClientSchema>;

/** Body for the on-demand VAT validation endpoint (form "Validate" button). */
export const validateVatSchema = z.object({
  vatNumber: vatNumberSchema,
  /** Fallback country (the client's billing country) used when the VAT has no prefix. */
  countryCode: z.string().trim().length(2).toUpperCase().optional(),
});
export type ValidateVatDto = z.infer<typeof validateVatSchema>;

/** Body for the duplicate pre-check the form runs before saving (warnings, never a refusal). */
export const checkClientDuplicatesSchema = z.object({
  email: emailSchema.optional(),
  phone: z.string().trim().max(40).optional(),
  excludeId: cuidSchema.optional(),
});
export type CheckClientDuplicatesDto = z.infer<typeof checkClientDuplicatesSchema>;

/**
 * Bulk reassignment of an agent's clients to another agent (Super Admin, Sales Manager).
 * Selection: all of the agent's clients, by province or city, clients with orders in the
 * last N months, or an explicit list. `toAgentId` is absent on the preview call.
 */
export const reassignSelectionSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('ALL') }),
  z.object({ mode: z.literal('PROVINCE'), value: z.string().trim().min(1).max(120) }),
  z.object({ mode: z.literal('CITY'), value: z.string().trim().min(1).max(120) }),
  z.object({ mode: z.literal('RECENT_ORDERS'), months: z.number().int().min(1).max(36).default(4) }),
  z.object({ mode: z.literal('IDS'), clientIds: z.array(cuidSchema).min(1).max(500) }),
]);
export type ReassignSelectionDto = z.infer<typeof reassignSelectionSchema>;

export const reassignClientsPreviewSchema = z.object({
  fromAgentId: cuidSchema,
  selection: reassignSelectionSchema,
});
export type ReassignClientsPreviewDto = z.infer<typeof reassignClientsPreviewSchema>;

export const reassignClientsSchema = reassignClientsPreviewSchema.extend({
  toAgentId: cuidSchema,
});
export type ReassignClientsDto = z.infer<typeof reassignClientsSchema>;
